// Coach-facing practice-slot sentences, as pure functions.
//
// These live here rather than inline in JSX for one specific reason: the same
// defect has now been shipped in THREE separate coach-facing surfaces, because
// each one hand-wrote its own version of the same sentence.
//
// The defect: announcing a re-home by LOCATION ONLY, never the new time. The
// container's `_rehome_score` ranks same-venue first, so the solver's most
// likely output is the same field at a different time. Rendered
// location-only, that becomes "Moving to Victory Park - Field 3" shown to a
// coach who already practices at Victory Park Field 3 — the one fact the
// feature exists to deliver, missing on the most probable case.
//
// It was fixed in `PracticeSlotCard.tsx` and in the email, and still shipped in
// the Home summary row, which no fix touched (found on staging 2026-07-28:
// Home's `assigned` branch read `current` and ignored `upcoming` entirely, so a
// pending re-home was invisible on the page a coach actually lands on).
//
// One function, one test file, both surfaces. `practiceCopy.test.ts` asserts
// that every line naming a place also names a time, so the fourth surface
// cannot reintroduce it.

import type { CoachPracticeSlot } from '@region13/shared-types'
import { shortDate } from './practiceDate'

/** A slot that has not started yet and is the team's ONLY slot
 *  (`upcoming_only`). Leads with the start date, because the coach's question
 *  is "when does this begin", then carries both the day/time and the place. */
export function practiceStartsLine(slot: CoachPracticeSlot): string {
  const starts = slot.effectiveFromDate ? shortDate(slot.effectiveFromDate) : 'a later date'
  return `Starts ${starts} · ${slot.when} · ${slot.where}`
}

/** A re-home: the team has a slot today AND a different one arriving. Names the
 *  new time as well as the new place, for the reason in this file's header. */
export function practiceMovingLine(slot: CoachPracticeSlot): string {
  const on = slot.effectiveFromDate ? ` on ${shortDate(slot.effectiveFromDate)}` : ''
  return `Moving to ${slot.when} · ${slot.where}${on}.`
}

/** Told to an Upper Division coach holding one of their two practices. States
 *  the gap plainly and promises the follow-up, matching the `unplaced` copy's
 *  shape. No em dash, no exclamation point (house style). */
export const SECOND_PRACTICE_PENDING =
  'Your second practice is not scheduled yet. We are still working on a field for it, and we will email you when there is one.'
